import { DateTime } from 'luxon'
import type TicketBatch from '@/types/TicketBatch'

export function parseDate(v: string | null): DateTime | null {
  if (!v) return null
  let dt = DateTime.fromISO(v)
  if (dt.isValid) return dt
  dt = DateTime.fromSQL(v)
  return dt.isValid ? dt : null
}

export function formatDate(value: string | null): string {
  if (!value) return '—'
  const dt = parseDate(value)
  return dt ? dt.toFormat('dd/MM HH:mm') : value
}

export function toFieldFormat(v: string | null): string | null {
  const dt = parseDate(v)
  return dt ? dt.toFormat('yyyy-MM-dd HH:mm:ss') : null
}

export function formatQuantity(batch: TicketBatch | null): string {
  if (!batch) return ''
  return batch.quantity !== null ? batch.quantity + 'un.' : 'Ilimitado'
}

export function formatPeriod(batch: TicketBatch | null): string {
  if (!batch) return ''
  if (!batch.starts_at && !batch.ends_at) return 'Sem período'
  return `${formatDate(batch.starts_at)} – ${formatDate(batch.ends_at)}`
}

// `exhausted` and `sold_out` share "Esgotado" on purpose: both mean out of
// stock. The API keeps them apart so a reader can tell an advance from a
// stalled channel without also reading `advanced_at`; nothing in the admin
// needs that difference. Same precedent as "Encerrado" carrying two senses.
const LABELS = {
  scheduled: 'Agendado',
  active: 'Ativo',
  sold_out: 'Esgotado',
  exhausted: 'Esgotado',
  expired: 'Encerrado',
} as const

// `exhausted` shares `sold_out`'s colour deliberately; see the note at LABELS.
const COLORS = {
  scheduled: 'info',
  active: 'success',
  sold_out: 'warning',
  exhausted: 'warning',
  expired: 'error',
} as const

/** Sold out, exhausted and expired are all terminal; only the reason differs. */
export function isClosed(batch: TicketBatch): boolean {
  return batch.status === 'sold_out'
    || batch.status === 'exhausted'
    || batch.status === 'expired'
}

export function statusLabel(batch: TicketBatch | null): string {
  return batch?.status ? LABELS[batch.status] : '—'
}

export function statusColor(batch: TicketBatch | null): string {
  return batch?.status ? COLORS[batch.status] : 'default'
}
